import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeStyle as _normalizeStyle, mergeProps as _mergeProps, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, Transition as _Transition } from "vue";
const _hoisted_1 = { class: "substatus-mgmt" };
const _hoisted_2 = {
    key: 0,
    class: "substatus-mgmt__locked"
};
const _hoisted_3 = { class: "substatus-mgmt__scroll" };
const _hoisted_4 = {
    key: 0,
    class: "substatus-mgmt__empty"
};
const _hoisted_5 = ["disabled", "aria-label"];
const _hoisted_6 = { class: "substatus-mgmt__palette" };
const _hoisted_7 = ["aria-label", "onClick"];
const _hoisted_8 = {
    key: 0,
    class: "substatus-mgmt__stale"
};
const _hoisted_9 = ["disabled"];
const _hoisted_10 = {
    key: 2,
    class: "substatus-mgmt__note"
};
const _hoisted_11 = {
    key: 1,
    class: "substatus-mgmt__save-bar"
};
const _hoisted_12 = {
    key: 0,
    class: "substatus-mgmt__msg substatus-mgmt__msg--error"
};
const _hoisted_13 = {
    key: 1,
    class: "substatus-mgmt__msg substatus-mgmt__msg--success"
};
import { computed, ref } from "vue";
import { TAG_COLORS, freshTagColor } from "@/constants/tagColors";
import { settingsService } from "@/services/settingsService";
import { useAuthStore, useRequirementsStore } from "@/stores";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SubstatusTagManagement',
    setup(__props) {
        const requirementsStore = useRequirementsStore();
        const authStore = useAuthStore();
        // Definitions change a column for the whole tenant, so editing is company-admin only, like the tenant-wide
        // Action Needed statuses. Everyone else gets the list read-only; the backend enforces the same rule.
        const canEdit = computed(() => Boolean(authStore.userInfo?.isAdmin));
        const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();
        const loaded = ref(false);
        const fieldsLoading = ref(false);
        const hoverIndex = ref(-1);
        const rows = ref([]);
        const rosterFields = ref([]);
        const initialSnapshot = ref("[]");
        let nextId = 0;
        // Roster fields come from the tenant's own patient rows, so the picker follows the file as it changes;
        // keys already in use stay listed even when the latest import no longer carries them.
        const fieldItems = computed(() => {
            const used = rows.value.map((row) => row.key).filter(Boolean);
            return Array.from(new Set([...rosterFields.value, ...used])).sort();
        });
        // The roster loader stores a CSV header as `header.replace(" ", "_").lower()`; a key typed here (rather than
        // picked) gets the same shape so "Authorization Status" finds the column the file actually produced.
        // Space by space, not run by run: Python replaces every occurrence, so the header "Auth  Status" becomes
        // `auth__status` in extra_data. Collapsing the run here would save a key that matches no field.
        function normalizeKey(key) {
            return (key || "").trim().replace(/ /g, "_").toLowerCase();
        }
        const liveFields = computed(() => new Set(rosterFields.value));
        function isRetired(row) {
            if (fieldsLoading.value || !rosterFields.value.length)
                return false;
            const key = normalizeKey(row.key);
            return Boolean(key) && !liveFields.value.has(key);
        }
        function serialize(list) {
            return list.map(({ key, label, color }) => ({ key: normalizeKey(key), label: (label || "").trim(), color }));
        }
        const hasChanges = computed(() => JSON.stringify(serialize(rows.value)) !== initialSnapshot.value);
        function labelFromKey(key) {
            return key
                .replace(/[_\s]+/g, " ")
                .trim()
                .replace(/\b\w/g, (c) => c.toUpperCase());
        }
        function onKeyChange(row) {
            row.key = normalizeKey(row.key);
            if (!row.label && row.key)
                row.label = labelFromKey(row.key);
        }
        function addRow() {
            rows.value.push({ id: nextId++, key: "", label: "", color: freshTagColor(rows.value.map((row) => row.color)) });
        }
        function validate(list) {
            const seen = new Set();
            for (const entry of list) {
                if (!entry.key || !entry.label)
                    return "Every substatus needs a roster field and a label.";
                if (seen.has(entry.key))
                    return `Roster field "${entry.key}" is used twice.`;
                seen.add(entry.key);
            }
            return null;
        }
        async function save() {
            const current = serialize(rows.value);
            const problem = validate(current);
            if (problem) {
                error.value = problem;
                return;
            }
            await runSave(async () => {
                await settingsService.updateSubstatusTags(current);
                await requirementsStore.refreshCurrentUserClientConfig();
                await load();
            }, { successText: "Substatus tags saved.", errorText: "Failed to save substatus tags." });
        }
        async function clearAll() {
            await runSave(async () => {
                await settingsService.resetSubstatusTags();
                await requirementsStore.refreshCurrentUserClientConfig();
                await load();
            }, { successText: "Substatus tags cleared.", errorText: "Failed to clear substatus tags." });
        }
        async function load() {
            try {
                const data = await settingsService.getSubstatusTags();
                rows.value = (data?.tags || []).map((tag) => ({ ...tag, id: nextId++ }));
                initialSnapshot.value = JSON.stringify(serialize(rows.value));
                loaded.value = true;
            }
            catch {
                error.value = "Failed to load substatus tags.";
            }
        }
        async function loadRosterFields() {
            fieldsLoading.value = true;
            try {
                const data = await settingsService.getSubstatusRosterFields();
                rosterFields.value = (data?.fields || []);
            }
            catch {
                rosterFields.value = [];
            }
            finally {
                fieldsLoading.value = false;
            }
        }
        load();
        if (canEdit.value)
            loadRosterFields();
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_combobox = _resolveComponent("v-combobox");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_spacer = _resolveComponent("v-spacer");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "substatus-mgmt__hint" }, [
                    _createTextVNode(" Each substatus reads one column of the patient roster file and refreshes with every roster import. Rows show it as "),
                    _createElementVNode("em", null, "Label: value"),
                    _createTextVNode(". Shared across Intake and Prior Auth. ")
                ], -1)),
                (!canEdit.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createVNode(_component_v_icon, {
                            icon: "mdi-lock-outline",
                            size: "16",
                            class: "substatus-mgmt__locked-icon"
                        }),
                        _cache[1] || (_cache[1] = _createElementVNode("span", null, "Substatus tags are managed by your company administrator. Contact them to add or change one.", -1))
                    ]))
                    : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_3, [
                    (loaded.value && !rows.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(canEdit.value
                            ? 'No substatus tags yet. Click "+ Add Substatus" below and pick a roster field.'
                            : "No substatus tags defined."), 1))
                        : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, index) => {
                        return (_openBlock(), _createBlock(_component_v_row, {
                            key: row.id,
                            dense: "",
                            align: "center",
                            class: "substatus-mgmt__row",
                            onMouseenter: ($event) => (hoverIndex.value = index),
                            onMouseleave: _cache[0] || (_cache[0] = ($event) => (hoverIndex.value = -1))
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_col, {
                                    cols: "auto",
                                    order: "1"
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_menu, { "close-on-content-click": true }, {
                                            activator: _withCtx(({ props: menuProps }) => [
                                                _createElementVNode("button", _mergeProps({ ref_for: true }, menuProps, {
                                                    type: "button",
                                                    class: "substatus-mgmt__dot",
                                                    disabled: !canEdit.value,
                                                    "aria-label": `Colour for ${row.label || row.key || 'new substatus'}`
                                                }), [
                                                    _createElementVNode("span", {
                                                        class: "substatus-mgmt__dot-inner",
                                                        style: _normalizeStyle({ background: _unref(TAG_COLORS)[row.color] || _unref(TAG_COLORS).grey })
                                                    }, null, 4)
                                                ], 16, _hoisted_5)
                                            ]),
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_6, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(TAG_COLORS), (hex, name) => {
                                                        return (_openBlock(), _createElementBlock("button", {
                                                            key: name,
                                                            type: "button",
                                                            class: _normalizeClass(["substatus-mgmt__palette-swatch", { 'substatus-mgmt__palette-swatch--active': row.color === name }]),
                                                            style: _normalizeStyle({ background: hex }),
                                                            "aria-label": `Use ${name}`,
                                                            onClick: ($event) => (row.color = name)
                                                        }, null, 14, _hoisted_7));
                                                    }), 128))
                                                ])
                                            ]),
                                            _: 2
                                        }, 1024)
                                    ]),
                                    _: 2
                                }, 1024),
                                _createVNode(_component_v_col, {
                                    cols: "12",
                                    sm: "",
                                    order: "3",
                                    "order-sm": "2"
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_row, { dense: "" }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_col, {
                                                    cols: "12",
                                                    sm: "7"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_combobox, {
                                                            modelValue: row.key,
                                                            "onUpdate:modelValue": [($event) => ((row.key) = $event), ($event) => (onKeyChange(row))],
                                                            items: fieldItems.value,
                                                            loading: fieldsLoading.value,
                                                            disabled: !canEdit.value,
                                                            density: "compact",
                                                            "hide-details": "",
                                                            placeholder: "Roster field"
                                                        }, null, 8, ["modelValue", "onUpdate:modelValue", "items", "loading", "disabled"]),
                                                        (isRetired(row))
                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, " The latest roster import does not offer this column. Its chips keep the last value the roster sent — remove this substatus to stop showing it. "))
                                                            : _createCommentVNode("", true)
                                                    ]),
                                                    _: 2
                                                }, 1024),
                                                _createVNode(_component_v_col, {
                                                    cols: "12",
                                                    sm: "5"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_text_field, {
                                                            modelValue: row.label,
                                                            "onUpdate:modelValue": ($event) => ((row.label) = $event),
                                                            disabled: !canEdit.value,
                                                            density: "compact",
                                                            "hide-details": "",
                                                            placeholder: "Label"
                                                        }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                                                    ]),
                                                    _: 2
                                                }, 1024)
                                            ]),
                                            _: 2
                                        }, 1024)
                                    ]),
                                    _: 2
                                }, 1024),
                                (canEdit.value)
                                    ? (_openBlock(), _createBlock(_component_v_col, {
                                        key: 0,
                                        cols: "auto",
                                        order: "2",
                                        "order-sm": "3",
                                        class: "ml-auto ml-sm-0"
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_btn, {
                                                icon: "mdi-close",
                                                variant: "text",
                                                size: "x-small",
                                                class: _normalizeClass(["substatus-mgmt__delete", { 'substatus-mgmt__delete--visible': hoverIndex.value === index }]),
                                                "aria-label": `Remove ${row.label || row.key || 'substatus'}`,
                                                onClick: ($event) => (rows.value.splice(index, 1))
                                            }, null, 8, ["class", "aria-label", "onClick"])
                                        ]),
                                        _: 2
                                    }, 1024))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 2
                        }, 1032, ["onMouseenter"]));
                    }), 128)),
                    (canEdit.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            type: "button",
                            class: "substatus-mgmt__add-btn",
                            disabled: !loaded.value,
                            onClick: addRow
                        }, " + Add Substatus ", 8, _hoisted_9))
                        : _createCommentVNode("", true),
                    (canEdit.value && loaded.value && !fieldsLoading.value && !rosterFields.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, " No roster fields found yet. They appear once the first patient roster file has been imported; you can still type a field name. "))
                        : _createCommentVNode("", true)
                ]),
                (canEdit.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createVNode(_component_v_btn, {
                            variant: "text",
                            size: "small",
                            disabled: !loaded.value,
                            onClick: clearAll
                        }, {
                            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                    _createTextVNode("Clear All", -1)
                                ]))]),
                            _: 1
                        }, 8, ["disabled"]),
                        _createVNode(_Transition, { name: "substatus-mgmt-fade" }, {
                            default: _withCtx(() => [
                                (_unref(error))
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_unref(error)), 1))
                                    : (_unref(successMsg))
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_unref(successMsg)), 1))
                                        : _createCommentVNode("", true)
                            ]),
                            _: 1
                        }),
                        _createVNode(_component_v_spacer),
                        _createVNode(_component_v_btn, {
                            variant: "flat",
                            size: "small",
                            color: "primary",
                            loading: _unref(saving),
                            disabled: !hasChanges.value,
                            onClick: save
                        }, {
                            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                    _createTextVNode(" Save ", -1)
                                ]))]),
                            _: 1
                        }, 8, ["loading", "disabled"])
                    ]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
